import { useState } from 'react';

import { api } from '../api/cliente';
import { useSesion } from '../auth/sesion';
import { Aviso, Campo, Encabezado, Etiqueta, Tarjeta } from '../ui/piezas';

/** La misma que exige la API. Repetirla aquí solo evita un viaje al servidor. */
const LARGO_MINIMO = 12;

/**
 * La cuenta de quien está dentro.
 *
 * Cambiar la contraseña cierra todas las sesiones, incluida ésta: la API borra
 * la cookie de refresco al terminar. Es lo correcto —si alguien la cambia
 * porque sospecha que se filtró, dejar viva la sesión del otro sería no haber
 * hecho nada— pero conviene decirlo antes, no después de echar a la persona.
 */
export default function MiCuenta() {
  const { usuario, salir } = useSesion();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  const cortaDeMas = nueva.length > 0 && nueva.length < LARGO_MINIMO;
  const noCoinciden = repetida.length > 0 && nueva !== repetida;
  const puedeEnviar =
    actual.length > 0 && nueva.length >= LARGO_MINIMO && nueva === repetida && !enviando;

  async function enviar() {
    setError(null);
    setEnviando(true);
    try {
      await api<void>('/auth/password', {
        metodo: 'POST',
        cuerpo: { passwordActual: actual, passwordNueva: nueva },
      });
      setListo(true);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  if (!usuario) return null;

  if (listo) {
    return (
      <>
        <Encabezado titulo="Mi cuenta" />
        <Tarjeta titulo="Contraseña cambiada">
          <p>
            Listo. Se cerraron todas las sesiones, también las de otros navegadores o
            dispositivos donde hubieras entrado.
          </p>
          <div style={{ height: 12 }} />
          <button onClick={() => void salir()}>Volver a entrar</button>
        </Tarjeta>
      </>
    );
  }

  return (
    <>
      <Encabezado titulo="Mi cuenta" />

      <Tarjeta titulo="Quién eres">
        <Etiqueta>Nombre</Etiqueta>
        <p>
          {usuario.nombre} {usuario.apellido}
        </p>
        <div style={{ height: 12 }} />
        <Etiqueta>Correo</Etiqueta>
        <p>{usuario.email}</p>
        <div style={{ height: 12 }} />
        <Etiqueta>Oficina</Etiqueta>
        <p>{usuario.sedeNombre}</p>
      </Tarjeta>

      <div style={{ height: 16 }} />

      <Tarjeta titulo="Cambiar la contraseña">
        <p style={{ opacity: 0.75 }}>
          Al cambiarla se cierran todas las sesiones, incluida ésta. Tendrás que volver a
          entrar con la nueva.
        </p>
        <div style={{ height: 16 }} />

        <Aviso error={error} />

        <Campo
          etiqueta="Contraseña actual"
          tipo="password"
          valor={actual}
          alCambiar={setActual}
          requerido
        />
        <div style={{ height: 12 }} />
        <Campo
          etiqueta="Contraseña nueva"
          tipo="password"
          valor={nueva}
          alCambiar={setNueva}
          requerido
          ayuda={
            cortaDeMas
              ? `Faltan ${LARGO_MINIMO - nueva.length} caracteres.`
              : `Mínimo ${LARGO_MINIMO} caracteres. Una frase larga protege más que símbolos raros.`
          }
        />
        <div style={{ height: 12 }} />
        <Campo
          etiqueta="Repite la nueva"
          tipo="password"
          valor={repetida}
          alCambiar={setRepetida}
          requerido
          ayuda={noCoinciden ? 'No coincide con la anterior.' : undefined}
        />
        <div style={{ height: 16 }} />

        <button onClick={() => void enviar()} disabled={!puedeEnviar}>
          {enviando ? 'Cambiando…' : 'Cambiar la contraseña'}
        </button>
      </Tarjeta>
    </>
  );
}
